import { Fragment, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { MoreVertical } from "lucide-react";
import Heading from "../../../shared_components/Heading/Heading";
import UserData from "../../../services/UserData";
import AIPreEnrichmentService from "../../../services/AIPreEnrichmentService";
import { DetailFields } from "../PreEnrichmentRuns/DetailFields";
import layout from "../IngestionBatches/IngestionBatches.module.css";
import tableStyle from "../PreEnrichmentRuns/PreEnrichmentRuns.module.css";
import style from "./PreEnrichmentRulesets.module.css";

const CreatedAt = ({ value }) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? <time dateTime={value} title={value}>{date.toLocaleString("en-GB")}</time> : "—";
};

const PreEnrichmentRulesets = () => {
  const userData = useContext(UserData);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setData(null);
    if (!userData) return () => { active = false; };
    AIPreEnrichmentService.getRulesets(userData)
      .then(result => { if (active) setData(result); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userData, retry]);

  return (
    <section className={layout.container} aria-label="Pre-Enrichment Rulesets" aria-busy={loading}>
      <Heading>Pre-Enrichment Rulesets</Heading>
      <div className={layout.loadingSlot}>
        {loading && <div className={layout.loadingBar} role="progressbar" aria-label="Loading pre-enrichment rulesets" />}
      </div>
      <div className={layout.content}>
        {error && <div role="alert">Unable to load pre-enrichment rulesets. <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRetry(value => value + 1)}>Retry</button></div>}
        {data && <table className={`${tableStyle.table} ${style.table}`}>
          <thead><tr>
            {["Ruleset", "Version", "Status", "Active", "Write mode", "Total rules", "Enabled", "Disabled", "Schema version", "Created at", "Actions"].map(title => <th scope="col" key={title}>{title}</th>)}
          </tr></thead>
          <tbody>
            {data.rulesets.map(ruleset => <Fragment key={ruleset.id}>
              <tr>
                <td>{ruleset.ruleset_id ?? "—"}</td>
                <td>{ruleset.version ?? "—"}</td>
                <td>{ruleset.summary?.status ?? "—"}</td>
                <td><span className={style.activePill} data-active={ruleset.active}>{ruleset.active === true ? "Active" : ruleset.active === false ? "Inactive" : "Not specified"}</span></td>
                <td>{ruleset.summary?.write_mode ?? "—"}</td>
                <td>{ruleset.summary?.total_rules ?? "—"}</td>
                <td>{ruleset.summary?.enabled_rules ?? "—"}</td>
                <td>{ruleset.summary?.disabled_rules ?? "—"}</td>
                <td>{ruleset.schema_version ?? "—"}</td>
                <td><CreatedAt value={ruleset.created_at} /></td>
                <td>
                  <Dropdown>
                    <Dropdown.Toggle variant="light" className={tableStyle.actionsToggle} id={`ruleset-actions-${ruleset.id}`} aria-label={`Actions for ruleset ${ruleset.version || ruleset.id}`}>
                      <MoreVertical size={22} aria-hidden="true" />
                    </Dropdown.Toggle>
                    {createPortal(<Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
                      <Dropdown.Item aria-expanded={!!expanded[ruleset.id]} aria-controls={`ruleset-${ruleset.id}`} onClick={() => setExpanded(value => ({ ...value, [ruleset.id]: !value[ruleset.id] }))}>View Details</Dropdown.Item>
                      <Dropdown.Item as={Link} to={`/intelligence/pre-enrichment-rulesets/${encodeURIComponent(ruleset.id)}/rules`}>Show Rules and Derivations</Dropdown.Item>
                    </Dropdown.Menu>, document.body)}
                  </Dropdown>
                </td>
              </tr>
              <tr hidden={!expanded[ruleset.id]} id={`ruleset-${ruleset.id}`}>
                <td colSpan={11} className={style.details}><DetailFields data={ruleset} /></td>
              </tr>
            </Fragment>)}
            {data.rulesets.length === 0 && <tr><td colSpan={11}>No pre-enrichment rulesets found.</td></tr>}
          </tbody>
        </table>}
      </div>
    </section>
  );
};

export default PreEnrichmentRulesets;
